import { V3ProductsDataSourceService } from './../../../core/data-sources/v3/products/v3-products-data-source.service';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionSelectionChange } from '@angular/material/core';
import { from, Observable, of, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

interface FilterOption {
  value: string;
  product: {
    sku: string;
    name: string;
    description: string;
  };
  isDescriptionSearch?: boolean;
  isFamily?: boolean;
}

@Component({
  selector: 'app-map-input-filter',
  templateUrl: './map-input-filter.component.html',
  styleUrls: ['./map-input-filter.component.scss']
})
export class MapInputFilterComponent implements OnInit {
  @Output() filterApplied = new EventEmitter<{ value: string, strict: boolean }>();
  @Output() filterCleared = new EventEmitter<boolean>();
  filteredOptions: Observable<FilterOption[]>;
  control = new FormControl('');
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  constructor(private v3ProductsDataSourceService: V3ProductsDataSourceService) { }

  ngOnInit(): void {
    this.filteredOptions = this.control.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      startWith(''),
      switchMap(value => {
        if (typeof value !== 'string' || !value || value.length <= 2) {
          return of([]);
        }
        return this.getAutocompleteSuggestions(value);
      })
    );
  }

  getAutocompleteSuggestions(term: string) {
    const skuNameSearch$ = from(this.v3ProductsDataSourceService.getV3SearchedProducts(term, ['sku', 'name']));
    const descriptionSearch$ = from(this.v3ProductsDataSourceService.getV3SearchedProducts(term, ['description']));

    return forkJoin({
      skuNameProducts: skuNameSearch$,
      descriptionProducts: descriptionSearch$
    }).pipe(
      map(({ skuNameProducts, descriptionProducts }) => {
        const skuNameOptions = skuNameProducts.map(p => ({
          value: term,
          product: p,
          isDescriptionSearch: false,
          isFamily: false
        }));

        const familyMap = new Map<string, any>();
        descriptionProducts.forEach(p => {
          if (p.description && !familyMap.has(p.description)) {
            familyMap.set(p.description, p);
          }
        });

        const familyOptions = Array.from(familyMap.values()).map(p => ({
          value: term,
          product: p,
          isDescriptionSearch: true,
          isFamily: true
        }));

        const descriptionProductOptions = descriptionProducts.map(p => ({
          value: term,
          product: p,
          isDescriptionSearch: true,
          isFamily: false
        }));
        const allOptions = [...familyOptions, ...descriptionProductOptions, ...skuNameOptions];
        const uniqueOptions = allOptions.filter((option, index, self) => {
          if (option.isFamily) return true;
          return index === self.findIndex(t => !t.isFamily && t.product.sku === option.product.sku);
        });

        return uniqueOptions;
      })
    );
  }

  /*
   * Fluxo geral ao selecionar uma opção do autocomplete:
   * - `option` é o valor associado à opção selecionada. Normalmente é um
   *   objeto `{ value: term, product: p, isDescriptionSearch?, isFamily? }`.
   * - Comportamento prioritário para preencher o input quando não for uma seleção de família:
   *     1) se o `product.sku` contém o `option.value`, usamos `product.sku`;
   *     2) senão, se o `product.name` contém o `option.value`, usamos `product.name`;
   *     3) senão, usamos `option.value`.
   *
   * Lógica de "família" (agrupamento por `description`):
   * - Ao buscar por `description` fazemos uma chamada separada à API para
   *   recuperar produtos com aquela descrição.
   * - Agrupamos resultados de `description` por texto de `description` único
   *   (map `familyMap`) e geramos uma opção do tipo `isFamily=true` para cada
   *   família encontrada. Essas opções representam o agrupamento e aparecem
   *   primeiro na lista do autocomplete.
   * - A seleção de uma `family` emite um (`strict: true`) com
   *   o valor igual à `description` para filtrar o mapa por toda a
   *   família (todos os produtos com aquela descrição).
   * - Mantemos também as opções de produtos individuais (sku/name) listadas abaixo das famílias; 
   * - removemos duplicatas por sku para evitar itens repetidos.
   */
  onOptionSelected(event: MatOptionSelectionChange) {
    if (!event.isUserInput) return;
    const option = event.source.value;

    if (option.isFamily) {
      this.control.setValue(option.product.description);
      this.filterApplied.emit({ value: option.product.description, strict: true });
      return;
    }

    const name = option.product.name.toLowerCase();
    const description = option.product.description?.toLowerCase() || '';
    const optionValue = option.value.toLowerCase();
    const sku = option.product.sku.toLowerCase();
    let value = '';

    if (option.isDescriptionSearch && description.includes(optionValue)) {
      value = option.product.sku;
    } else if (sku.includes(optionValue)) {
      value = option.product.sku;
    } else if (name.includes(optionValue)) {
      value = option.product.name;
    } else {
      value = option.value;
    }

    this.control.setValue(value);
    this.filterApplied.emit({ value: this.control.value, strict: true });
  }

  /*
   * Comportamento:
   * - Se `option` conter isFamily, retorna `description` do produto.
   * - Se `option` for falso (null/undefined) retorna string vazia.
   * - Se `option` for um objeto, verifica se o `value` buscado está
   *   contido no `sku` do produto; se sim retorna o `sku`.
   * - Caso contrário, se o `value` estiver contido no `name` do produto,
   *   retorna o `name`.
   * - Se nenhuma das condições acima for verdadeira, retorna `option.value`.
   * - Se `option` não for objeto (ex: já for string), retorna `option`.
  */
  autocompleteDisplayedValue(option): string {
    if (!option) return '';
    if (typeof option === 'object') {
      if (option.isFamily) {
        return option.product.description;
      }

      const sku = option.product.sku.toLowerCase();
      const name = option.product.name.toLowerCase();
      const description = option.product.description?.toLowerCase() || '';
      const value = option.value.toLowerCase();

      if (option.isDescriptionSearch && description.includes(value)) {
        return option.product.sku;
      } else if (sku.includes(value)) {
        return option.product.sku;
      } else if (name.includes(value)) {
        return option.product.name;
      } else {
        return option.value;
      }
    }
    return option;
  }

  /**
   * Aplica o filtro com base no valor atual do input.
   * Fecha o painel de autocomplete caso esteja aberto.
   * Emite o evento `change` com o valor atual do FormControl.
   */
  applyFilter() {
    this.autocompleteTrigger?.closePanel();
    this.filterApplied.emit({ value: this.control.value, strict: false });
  }

  clearFilter() {
    this.control.setValue('');
    this.filterCleared.emit(true);
  }

}
